import { Share2, Facebook, Linkedin, MessageCircle, Link2, MessageSquare } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { useToast } from "@/hooks/use-toast";

interface ShareButtonsProps {
  headline: string;
  articleId?: string;
  url?: string;
}

const ShareButtons = ({ headline, articleId, url }: ShareButtonsProps) => {
  const { toast } = useToast();
  
  // Generate URL to our website for sharing (not the original source)
  const shareUrl = articleId 
    ? `${window.location.origin}/news/${articleId}`
    : window.location.href;

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      toast({
        title: "Link copied!",
        description: "Article link has been copied to clipboard",
      });
    } catch (err) {
      toast({
        title: "Failed to copy",
        description: "Please try again",
        variant: "destructive",
      });
    }
  };

  const shareLinks = {
    twitter: `https://x.com/intent/tweet?text=${encodeURIComponent(`${headline}\n\nSee more on @bitcoinmindt`)}&url=${encodeURIComponent(shareUrl)}`,
    facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`,
    linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`,
    reddit: `https://reddit.com/submit?url=${encodeURIComponent(shareUrl)}&title=${encodeURIComponent(headline)}`,
    whatsapp: `https://wa.me/?text=${encodeURIComponent(headline + ' ' + shareUrl)}`,
  };

  const openShareWindow = (platform: keyof typeof shareLinks) => {
    window.open(shareLinks[platform], '_blank', 'width=600,height=400');
  };

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button variant="ghost" size="sm" className="gap-2">
          <Share2 className="h-4 w-4" />
          Share
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-56">
        <div className="space-y-2">
          <p className="text-sm font-medium mb-3">Share this article</p>
          
          <Button
            variant="ghost"
            className="w-full justify-start gap-2"
            onClick={() => openShareWindow('twitter')}
            data-share-platform="twitter"
          >
            <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
              <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
            </svg>
            X (Twitter)
          </Button>

          <Button
            variant="ghost"
            className="w-full justify-start gap-2"
            onClick={() => openShareWindow('facebook')}
            data-share-platform="facebook"
          >
            <Facebook className="h-4 w-4" />
            Facebook
          </Button>

          <Button
            variant="ghost"
            className="w-full justify-start gap-2"
            onClick={() => openShareWindow('linkedin')}
            data-share-platform="linkedin"
          >
            <Linkedin className="h-4 w-4" />
            LinkedIn
          </Button>

          <Button
            variant="ghost"
            className="w-full justify-start gap-2"
            onClick={() => openShareWindow('reddit')}
            data-share-platform="reddit"
          >
            <MessageCircle className="h-4 w-4" />
            Reddit
          </Button>

          <Button
            variant="ghost"
            className="w-full justify-start gap-2"
            onClick={() => openShareWindow('whatsapp')}
            data-share-platform="whatsapp"
          >
            <MessageSquare className="h-4 w-4" />
            WhatsApp
          </Button>

          <div className="border-t pt-2 mt-2">
            <Button
              variant="ghost"
              className="w-full justify-start gap-2"
              onClick={handleCopyLink}
              data-share-platform="copy"
            >
              <Link2 className="h-4 w-4" />
              Copy Link
            </Button>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
};

export default ShareButtons;
